import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

// Reusable content section + heading primitives so every page keeps the same
// rhythm, tones and section-title treatment. Use these instead of re-styling
// per page.
//
// Tones: light (white), mist/muted (steel-blue tint from the logo leaf), dark
// (deep petrol band). The heading underline is the tricolor bar — the
// petrol/green/red of the QAS logo — used as the site's signature mark.

const TONES = {
  light: { bg: '#ffffff', heading: '#00425c', body: '#48606d' },
  muted: { bg: '#e9f1f6', heading: '#00425c', body: '#48606d' },
  mist: { bg: '#e9f1f6', heading: '#00425c', body: '#48606d' },
  dark: { bg: '#00425c', heading: '#ffffff', body: 'rgba(255,255,255,0.78)' },
};

export const TRICOLOR =
  'linear-gradient(90deg, #00425c 0%, #00425c 34%, #86a315 34%, #86a315 67%, #c10a27 67%, #c10a27 100%)';

export const Section = styled(Box, {
  shouldForwardProp: (p) => p !== 'tone',
})(({ theme, tone = 'light' }) => ({
  backgroundColor: (TONES[tone] || TONES.light).bg,
  color: (TONES[tone] || TONES.light).body,
  padding: '96px 0',
  position: 'relative',
  ...(tone === 'dark' && {
    backgroundImage:
      'radial-gradient(circle at 12% 18%, rgba(134,163,21,0.16), transparent 42%), radial-gradient(circle at 88% 85%, rgba(193,10,39,0.11), transparent 45%), radial-gradient(circle at 70% 10%, rgba(128,172,192,0.14), transparent 38%)',
  }),
  [theme.breakpoints.down('md')]: {
    padding: '64px 0',
  },
}));

const Kicker = styled(Typography, {
  shouldForwardProp: (p) => p !== 'tone',
})(({ theme, tone = 'light' }) => ({
  color: tone === 'dark' ? '#8fc1d6' : '#86a315',
  fontFamily: '"Sora", sans-serif',
  fontSize: '13px',
  fontWeight: 700,
  letterSpacing: '2.5px',
  textTransform: 'uppercase',
  marginBottom: '14px',
}));

const Heading = styled(Typography, {
  shouldForwardProp: (p) => p !== 'align' && p !== 'tone',
})(({ theme, align = 'center', tone = 'light' }) => ({
  position: 'relative',
  fontWeight: 700,
  color: (TONES[tone] || TONES.light).heading,
  paddingBottom: '22px',
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: align === 'center' ? '50%' : 0,
    transform: align === 'center' ? 'translateX(-50%)' : 'none',
    width: '72px',
    height: '4px',
    borderRadius: '4px',
    backgroundImage: TRICOLOR,
  },
  [theme.breakpoints.down('md')]: {
    '&:after': { left: '50%', transform: 'translateX(-50%)' },
  },
}));

const Sub = styled(Typography, {
  shouldForwardProp: (p) => p !== 'tone',
})(({ theme, tone = 'light' }) => ({
  color: (TONES[tone] || TONES.light).body,
  fontSize: '17px',
  lineHeight: 1.75,
  maxWidth: '760px',
  marginTop: '20px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '15.5px',
  },
}));

// Section heading with kicker, tricolor-underlined title and optional subtitle.
export function SectionHeading({ kicker, title, subtitle, align = 'center', tone = 'light', maxWidth = '860px', sx }) {
  const centered = align === 'center';
  return (
    <Box
      sx={{
        textAlign: { xs: 'center', md: centered ? 'center' : 'left' },
        maxWidth: centered ? maxWidth : 'none',
        mx: centered ? 'auto' : 0,
        mb: 6,
        ...sx,
      }}
    >
      {kicker && <Kicker tone={tone}>{kicker}</Kicker>}
      <Heading variant="h2" align={align} tone={tone}>
        {title}
      </Heading>
      {subtitle && (
        <Sub tone={tone} sx={{ mx: { xs: 'auto', md: centered ? 'auto' : 0 } }}>
          {subtitle}
        </Sub>
      )}
    </Box>
  );
}

export { Container };
export default Section;
